/**
 * Upload a photo or video from the device into the Account's vault so it can
 * be sent like any vault item.
 *
 * OnlyFans has no "upload to Vault": signed → S3 → converter yields only a
 * processId. Media enters the Vault when used (ADR 0026): Save for later post
 * → read vault ids from the response → delete the post.
 */
import { crmFetch } from './api';

type Raw = Record<string, unknown>;

export interface UploadedMedia {
  id: string;
  type: 'photo' | 'video' | 'audio';
  label: string;
}

export type MediaObject = {
  processId: string;
  host: string;
  name?: string;
  extra?: unknown;
  additional?: unknown;
  thumbs?: unknown;
  duration?: unknown;
  [key: string]: unknown;
};

function unwrap(payload: unknown): Raw {
  let cur = payload as Raw;
  for (let i = 0; i < 4; i++) {
    if (!cur || typeof cur !== 'object') break;
    if (cur.data != null && (cur.ok === true || cur.ok === false || 'operation' in cur || 'status_code' in cur)) {
      cur = cur.data as Raw;
      continue;
    }
    break;
  }
  return (cur && typeof cur === 'object' ? cur : {}) as Raw;
}

function numericId(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 1000) return String(Math.floor(value));
  const s = String(value ?? '').trim();
  return /^\d{4,}$/.test(s) ? s : null;
}

/** A vault id named anywhere in an upload response (media_id, vault_id, content_id …; `id` too when asked). */
export function mediaIdIn(payload: unknown, opts: { plainId?: boolean } = {}): string | null {
  const keys = ['media_id', 'mediaId', 'vault_id', 'vaultId', 'content_id', 'contentId'];
  const stack: unknown[] = [unwrap(payload), payload];
  const seen = new Set<unknown>();
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    for (const k of keys) {
      const hit = numericId((cur as Raw)[k]);
      if (hit) return hit;
    }
    if (opts.plainId) {
      const hit = numericId((cur as Raw).id);
      if (hit) return hit;
    }
    for (const v of Object.values(cur as Raw)) if (v && typeof v === 'object') stack.push(v);
  }
  return null;
}

/** Converter media object (processId + host) from a gateway/CDN response. */
export function mediaObjectFrom(payload: unknown): MediaObject | null {
  const stack: unknown[] = [unwrap(payload), payload];
  const seen = new Set<unknown>();
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    const row = cur as Raw;
    const processId = String(row.processId ?? row.process_id ?? '').trim();
    const host = String(row.host ?? '').trim();
    if (processId && host) {
      return {
        ...row,
        processId,
        host,
        name: row.name != null ? String(row.name) : undefined,
        extra: row.extra,
        additional: row.additional,
        thumbs: row.thumbs,
        duration: row.duration,
      };
    }
    for (const key of ['media', 'file', 'upload', 'data', 'result', 'body']) {
      if (row[key] && typeof row[key] === 'object') stack.push(row[key]);
    }
    for (const v of Object.values(row)) if (v && typeof v === 'object') stack.push(v);
  }
  return null;
}

/** OF Save for later body that materializes vault ids from converter objects (ADR 0026). */
export function saveForLaterBody(media: MediaObject | MediaObject[]): Record<string, unknown> {
  const list = Array.isArray(media) ? media : [media];
  return {
    preview: [],
    text: '',
    mediaFiles: list,
    isScheduled: 1,
    isPublishedWithPeriod: 1,
  };
}

/** First vault media id on a create-post response. */
export function vaultIdFromPost(payload: unknown): string | null {
  const stack: unknown[] = [unwrap(payload), payload];
  const seen = new Set<unknown>();
  while (stack.length) {
    const cur = stack.pop();
    if (!cur || typeof cur !== 'object' || seen.has(cur)) continue;
    seen.add(cur);
    const row = cur as Raw;
    if (Array.isArray(row.media) && row.media.length) {
      const id = numericId((row.media[0] as Raw)?.id);
      if (id) return id;
    }
    for (const v of Object.values(row)) if (v && typeof v === 'object') stack.push(v);
  }
  return null;
}

function postIdFrom(payload: unknown): string | null {
  const d = unwrap(payload);
  const id = d.id ?? (d as Raw).post_id ?? (d as Raw).postId;
  if (id == null || id === '') return null;
  return String(id);
}

function etagFrom(payload: unknown): string {
  const d = unwrap(payload);
  const raw = d.etag ?? d.ETag ?? (d.body && typeof d.body === 'object' ? (d.body as Raw).etag ?? (d.body as Raw).ETag : '');
  return String(raw ?? '').trim();
}

/**
 * Metadata-only form for `POST convert.onlyfans.com/file/upload` after the S3 PUT
 * (live capture 2026-10-08). Presets match OF web of_beta defaults.
 */
export function converterFormFields(opts: {
  signed: unknown;
  etag: string;
  fileName: string;
  authId?: string;
}): Record<string, string> | null {
  const d = unwrap(opts.signed);
  const putUrl = String(d.putUrl ?? d.put_url ?? '').trim();
  const key = String(d.key ?? '').trim();
  const uploadToken = String(d.uploadToken ?? d.upload_token ?? '').trim();
  const etag = String(opts.etag ?? '').trim();
  if (!putUrl || !key || !uploadToken || !etag) return null;
  const authId = String(opts.authId || key.split('/')[1] || '').trim();
  const quoted = etag.includes('"') ? etag : `"${etag.replace(/^"|"$/g, '')}"`;
  return {
    preset: 'of_beta',
    protected_preset: 'of_drm',
    preset_public: 'of_public',
    preset_png: 'of_png',
    isDelay: 'true',
    needThumbs: 'true',
    'additional[user]': authId,
    'file[ETag]': quoted,
    'file[Location]': putUrl.split('?')[0] || putUrl,
    'file[Key]': key,
    'file[Bucket]': 'of2transcoder',
    'file[name]': opts.fileName || 'upload',
    'file[uploadToken]': uploadToken,
    'file[secure]': 'false',
  };
}

type Plan =
  | { kind: 'post'; url: string; fields: Record<string, unknown> }
  | { kind: 'put'; url: string; contentType: string; headers: Record<string, string> };

/** Where and how the gateway wants the file uploaded (the shapes chat-upload.js has met). */
export function uploadPlan(signed: unknown, contentType: string): Plan | null {
  const d = unwrap(signed);
  const up = (d.upload && typeof d.upload === 'object' ? d.upload : {}) as Raw;
  const host = (d.host || up.host) as string | undefined;
  const rel = (d.url || d.path || up.url) as string | undefined;
  const fields = (d.fields || d.form || d.formData || up.fields) as Record<string, unknown> | undefined;
  const join = (base: string, path: string) => `${base.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  if (host && rel && fields && typeof fields === 'object') return { kind: 'post', url: join(String(host), String(rel)), fields };
  const rawUrl = (d.putUrl || d.put_url || d.uploadUrl || d.upload_url || d.url) as string | undefined;
  if (rawUrl && fields && typeof fields === 'object') {
    return { kind: 'post', url: String(rawUrl).startsWith('http') ? String(rawUrl) : join('https://convert.onlyfans.com', String(rawUrl)), fields };
  }
  if (rawUrl) {
    return {
      kind: 'put',
      url: String(rawUrl),
      contentType: String(d.contentType || d.content_type || contentType || 'application/octet-stream'),
      headers: (d.headers as Record<string, string>) || {},
    };
  }
  return null;
}

async function relayUpload(accountId: string, plan: Plan, file: File): Promise<unknown> {
  const fd = new FormData();
  fd.append('url', plan.url);
  fd.append('account_id', accountId);
  if (plan.kind === 'post') {
    fd.append('method', 'POST');
    fd.append('form_fields', JSON.stringify(plan.fields));
    fd.append('content_type', file.type || 'application/octet-stream');
  } else {
    fd.append('method', 'PUT');
    fd.append('content_type', plan.contentType || file.type || 'application/octet-stream');
    if (Object.keys(plan.headers).length) fd.append('headers', JSON.stringify(plan.headers));
  }
  fd.append('file', file, file.name || 'upload');
  const body = await crmFetch('/v1/upload/cdn-relay', { accountId, local: true, body: fd });
  const raw = unwrap(body);
  return raw.body && typeof raw.body === 'object' ? { ...raw, ...(raw.body as Raw) } : raw;
}

/** Metadata-only POST (converter handoff) through the CDN relay. */
async function relayFormOnly(accountId: string, url: string, fields: Record<string, string>): Promise<unknown> {
  const fd = new FormData();
  fd.append('url', url);
  fd.append('account_id', accountId);
  fd.append('method', 'POST');
  fd.append('form_only', '1');
  fd.append('form_fields', JSON.stringify(fields));
  const body = await crmFetch('/v1/upload/cdn-relay', { accountId, local: true, body: fd });
  const raw = unwrap(body);
  return raw.body && typeof raw.body === 'object' ? raw.body : raw;
}

interface VaultRow {
  id: string;
  type: string;
  createdMs: number;
  ready: boolean;
  hasError: boolean;
}

async function recentVault(accountId: string): Promise<VaultRow[]> {
  const body = await crmFetch('/v1/vault/media', {
    accountId,
    body: { list_id: 'all', limit: 64, offset: 0, field: 'recent', sort: 'desc' },
  });
  const raw = unwrap(body);
  const list = (Array.isArray(raw.list) ? raw.list : Array.isArray(raw.items) ? raw.items : Array.isArray(raw) ? raw : []) as Raw[];
  return list
    .map((r) => ({
      id: numericId(r.media_id ?? r.mediaId ?? r.id) || '',
      type: String(r.type || '').toLowerCase(),
      createdMs: Date.parse(String(r.created_at ?? r.createdAt ?? r.date ?? '')) || 0,
      ready: r.isReady === true || r.is_ready === true,
      hasError: r.hasError === true,
    }))
    .filter((r) => r.id);
}

function typeMatches(rowType: string, type: 'photo' | 'video' | 'audio'): boolean {
  return rowType.includes(type) || (type === 'photo' && rowType === 'image') || (type === 'audio' && rowType.includes('voice'));
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Ids already handed to another in-flight upload (parallel picks must not steal each other). */
const claimedVaultIds = new Set<string>();

function claimVaultId(id: string): boolean {
  if (!id || claimedVaultIds.has(id)) return false;
  claimedVaultIds.add(id);
  return true;
}

/** How long to wait for `isReady` before accepting a still-processing vault row. Photos appear quickly; videos need the converter. */
export function pendingGraceMs(type: 'photo' | 'video' | 'audio'): number {
  if (type === 'photo') return 1_500;
  if (type === 'video') return 4_000;
  return 8_000;
}

export function pollDelayMs(elapsedMs: number): number {
  return elapsedMs < 8_000 ? 400 : 900;
}

function pickFreshVaultId(
  rows: VaultRow[],
  opts: { type: 'photo' | 'video' | 'audio'; known: Set<string>; allowPending: boolean },
): string | null {
  const fresh = rows
    .filter((r) => !r.hasError && !opts.known.has(r.id) && !claimedVaultIds.has(r.id) && typeMatches(r.type, opts.type))
    .sort((a, b) => b.createdMs - a.createdMs);
  const hit = fresh.find((r) => r.ready) || (opts.allowPending ? fresh[0] : undefined);
  return hit?.id ?? null;
}

async function hashLookup(accountId: string, etag: string, size: number): Promise<string | null> {
  const h = etag.replace(/^"|"$/g, '');
  if (!h || !size) return null;
  try {
    const body = await crmFetch('/v1/of/raw', {
      accountId,
      body: {
        method: 'GET',
        api_path: `/api2/v2/vault/media/hash?h=${encodeURIComponent(h)}&size=${size}`,
        referer: 'https://onlyfans.com/',
        body: null,
      },
    });
    return mediaIdIn(body, { plainId: true });
  } catch {
    return null;
  }
}

async function convertAfterPut(accountId: string, signed: unknown, uploaded: unknown, file: File): Promise<MediaObject | null> {
  const existing = mediaObjectFrom(uploaded);
  if (existing) return existing;
  const etag = etagFrom(uploaded);
  const fields = converterFormFields({ signed, etag, fileName: file.name || 'upload' });
  if (!fields) return null;
  const hosts = ['https://convert.onlyfans.com/file/upload', 'https://convert1.onlyfans.com/file/upload', 'https://convert2.onlyfans.com/file/upload'];
  let lastErr: unknown;
  for (const url of hosts) {
    try {
      const res = await relayFormOnly(accountId, url, fields);
      const obj = mediaObjectFrom(res);
      if (obj) return obj;
      lastErr = new Error('Converter returned no processId');
    } catch (e) {
      lastErr = e;
    }
  }
  if (lastErr) throw lastErr;
  return null;
}

export function isPostSpacingError(message: string): boolean {
  return /allow\s+\d+\s+seconds|подождите|please wait/i.test(message);
}

export function isDailyLimitError(message: string): boolean {
  return /daily limit|дневн(ой|ого)\s+лимит/i.test(message);
}

/**
 * Materialize a converter object into a vault id via Save for later + delete (ADR 0026).
 * Retries OF's 10s spacing; surfaces a clear error on the daily post quota.
 */
export async function commitMediaObjectToVault(accountId: string, media: MediaObject): Promise<string> {
  let created: unknown;
  let lastErr: Error | null = null;
  for (let attempt = 0; attempt < 4; attempt++) {
    try {
      created = await crmFetch('/v1/of/raw', {
        accountId,
        body: {
          method: 'POST',
          api_path: '/api2/v2/posts',
          referer: 'https://onlyfans.com/posts/create',
          body: saveForLaterBody(media),
        },
      });
      lastErr = null;
      break;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      if (isDailyLimitError(err.message)) {
        throw new Error(
          'OnlyFans daily post limit reached for this account (queue is full). Try another account or wait until tomorrow.',
        );
      }
      if (isPostSpacingError(err.message) && attempt < 3) {
        await sleep(12_000);
        lastErr = err;
        continue;
      }
      throw err;
    }
  }
  if (lastErr) throw lastErr;
  const vaultId = vaultIdFromPost(created);
  const postId = postIdFrom(created);
  if (postId) {
    try {
      await crmFetch('/v1/of/raw', {
        accountId,
        body: {
          method: 'DELETE',
          api_path: `/api2/v2/posts/${postId}`,
          referer: 'https://onlyfans.com/',
          body: null,
        },
      });
    } catch {
      // Media stays in vault even if delete fails; leftover queue item is recoverable.
    }
  }
  if (!vaultId) throw new Error('OnlyFans did not return a vault id for this upload');
  return vaultId;
}

/**
 * Signed → CDN → converter only (no Save for later). Stories and similar
 * "use" endpoints accept the processId object directly.
 */
export async function stageMediaObject(
  accountId: string,
  fanId: string,
  file: File,
  onStage?: (stage: 'uploading' | 'processing') => void,
): Promise<MediaObject> {
  const contentType = file.type || 'application/octet-stream';
  const rand = Math.random().toString(36).slice(2, 10);
  const safe = (file.name || 'file').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'file';
  onStage?.('uploading');
  const signed = await crmFetch('/v1/upload/signed/create', {
    accountId,
    body: { key: `upload/${rand}/${safe}`, content_type: contentType, parts: 1, secure: false, fan_id: fanId || null },
  });
  const plan = uploadPlan(signed, contentType);
  if (!plan) throw new Error('The upload service did not return an upload address');
  const uploaded = await relayUpload(accountId, plan, file);
  onStage?.('processing');
  let media = mediaObjectFrom(uploaded) || mediaObjectFrom(signed);
  if (!media && plan.kind === 'put') media = await convertAfterPut(accountId, signed, uploaded, file);
  if (!media) throw new Error('OnlyFans converter did not return a processId');
  return media;
}

/** Upload `file`, wait for OnlyFans to put it in the vault, return its vault id. */
export async function uploadToVault(accountId: string, fanId: string, file: File, onStage?: (stage: 'uploading' | 'processing') => void): Promise<UploadedMedia> {
  const contentType = file.type || 'application/octet-stream';
  const type: 'photo' | 'video' = contentType.startsWith('video/') ? 'video' : 'photo';
  const rand = Math.random().toString(36).slice(2, 10);
  const safe = (file.name || 'file').replace(/[^\w.-]+/g, '_').slice(0, 80) || 'file';

  onStage?.('uploading');
  const [vaultSnap, signed] = await Promise.all([
    recentVault(accountId).catch(() => [] as VaultRow[]),
    crmFetch('/v1/upload/signed/create', {
      accountId,
      body: { key: `upload/${rand}/${safe}`, content_type: contentType, parts: 1, secure: false, fan_id: fanId || null },
    }),
  ]);
  const known = new Set(vaultSnap.filter((r) => typeMatches(r.type, type)).map((r) => r.id));
  for (const id of claimedVaultIds) known.add(id);

  const plan = uploadPlan(signed, contentType);
  if (!plan) throw new Error('The upload service did not return an upload address');
  const uploaded = await relayUpload(accountId, plan, file);
  if (type === 'video') {
    void crmFetch('/v1/log', {
      accountId,
      body: { message: 'Send data to converter', level: 'debug', context: { name: file.name, mediaType: type }, fan_id: fanId || null },
    }).catch(() => undefined);
  }

  const direct = mediaIdIn(uploaded, { plainId: true }) || mediaIdIn(uploaded);
  if (direct && !known.has(direct) && claimVaultId(direct)) {
    return { id: direct, type, label: file.name };
  }

  const etag = etagFrom(uploaded);
  if (etag) {
    const reused = await hashLookup(accountId, etag, file.size);
    if (reused && claimVaultId(reused)) return { id: reused, type, label: file.name };
  }

  onStage?.('processing');
  let media = mediaObjectFrom(uploaded) || mediaObjectFrom(signed);
  if (!media && plan.kind === 'put') {
    media = await convertAfterPut(accountId, signed, uploaded, file);
  }
  if (media) {
    const id = await commitMediaObjectToVault(accountId, media);
    if (claimVaultId(id) || !known.has(id)) {
      claimedVaultIds.add(id);
      return { id, type, label: file.name };
    }
    return { id, type, label: file.name };
  }

  // Last resort: short vault poll (legacy path; usually empty until media is "used").
  const started = Date.now();
  const grace = pendingGraceMs(type);
  while (Date.now() - started < 15_000) {
    const elapsed = Date.now() - started;
    const id = pickFreshVaultId(await recentVault(accountId), { type, known, allowPending: elapsed >= grace });
    if (id && claimVaultId(id)) return { id, type, label: file.name };
    await sleep(pollDelayMs(elapsed));
  }
  throw new Error('OnlyFans is still processing this file. Wait a moment and try again.');
}

function toBase64(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result || '');
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

function wavFrom(buffer: AudioBuffer): Blob {
  const channels = buffer.numberOfChannels;
  const rate = buffer.sampleRate;
  const frames = buffer.length;
  const out = new DataView(new ArrayBuffer(44 + frames * channels * 2));
  const text = (o: number, v: string) => [...v].forEach((c, i) => out.setUint8(o + i, c.charCodeAt(0)));
  text(0, 'RIFF');
  out.setUint32(4, 36 + frames * channels * 2, true);
  text(8, 'WAVE');
  text(12, 'fmt ');
  out.setUint32(16, 16, true);
  out.setUint16(20, 1, true);
  out.setUint16(22, channels, true);
  out.setUint32(24, rate, true);
  out.setUint32(28, rate * channels * 2, true);
  out.setUint16(32, channels * 2, true);
  out.setUint16(34, 16, true);
  text(36, 'data');
  out.setUint32(40, frames * channels * 2, true);
  const data = Array.from({ length: channels }, (_, c) => buffer.getChannelData(c));
  let o = 44;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < channels; c++) {
      const v = Math.max(-1, Math.min(1, data[c]![i]!));
      out.setInt16(o, v < 0 ? v * 0x8000 : v * 0x7fff, true);
      o += 2;
    }
  }
  return new Blob([out.buffer], { type: 'audio/wav' });
}

async function asWav(file: File): Promise<File> {
  if (/wav/i.test(file.type) || /\.wav$/i.test(file.name)) return file;
  const ctx = new AudioContext();
  try {
    const buffer = await ctx.decodeAudioData((await file.arrayBuffer()).slice(0));
    return new File([wavFrom(buffer)], `${file.name.replace(/\.\w+$/, '') || `voice-${Date.now()}`}.wav`, { type: 'audio/wav' });
  } finally {
    await ctx.close().catch(() => undefined);
  }
}

async function postVoice(accountId: string, fanId: string, file: File): Promise<unknown> {
  return crmFetch('/v1/media/upload', {
    accountId,
    body: {
      filename: file.name || `voice-${Date.now()}.webm`,
      content_type: file.type || 'audio/webm',
      file_base64: await toBase64(file),
      media_type: 'audio',
      secure: false,
      fan_id: fanId || null,
      with_log: true,
    },
  });
}

/** OnlyFans' transcoder handoff for a fresh upload: what `media_files` of a send takes. */
export type MediaHandoff = Record<string, unknown> & { processId: string; host: string };

export interface UploadedVoice {
  handoff: MediaHandoff;
  type: 'audio';
  label: string;
}

/** The handoff object in a /v1/media/upload answer ({ok, data: {media: {processId, host, name, extra}}}). */
export function voiceHandoff(payload: unknown): MediaHandoff | null {
  const media = unwrap(payload).media as Raw | undefined;
  if (!media || typeof media !== 'object') return null;
  const processId = String(media.processId ?? '').trim();
  const host = String(media.host ?? '').trim();
  return processId && host ? ({ ...media, processId, host } as MediaHandoff) : null;
}

/**
 * Upload a recorded or created voice note: the gateway's one-shot media upload,
 * retried as WAV when OnlyFans refuses the recording's own format. OnlyFans only
 * puts such a file in the vault once a message carries it, so the send takes the
 * transcoder handoff itself (as the AI's voice notes do), not a vault id.
 */
export async function uploadVoice(accountId: string, fanId: string, file: File): Promise<UploadedVoice> {
  let body: unknown;
  try {
    body = await postVoice(accountId, fanId, file);
  } catch (first) {
    try {
      body = await postVoice(accountId, fanId, await asWav(file));
    } catch (second) {
      throw new Error((second as Error).message || (first as Error).message || 'Voice upload failed');
    }
  }
  const handoff = voiceHandoff(body);
  if (!handoff) throw new Error('OnlyFans did not accept the voice note. Try again.');
  return { handoff, type: 'audio', label: file.name };
}
